import { create } from 'zustand';

const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';

export type UserRole = 'superadmin' | 'admin' | 'analyst' | 'viewer';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  tenant_id: string;
  modules: string[];
  impersonated_by?: string;
  /** Whether this account has TOTP MFA enrolled. Carried in the JWT and the
   *  login response so it survives silent refresh / reload. */
  mfa_enabled?: boolean;
  /** Which authority performed THIS SESSION's second factor: 'idp' when the
   *  customer's identity provider proved it (a verified id_token under a
   *  delegated SSO link — see backend/auth_service/sso_policy.py), 'totp' when
   *  our own enrollment did, undefined when no second factor happened.
   *
   *  Presentation only. It suppresses an enrollment prompt that would
   *  otherwise be shown; it never grants access. Enforcement is the
   *  `mfa_pending` claim, checked by every service's auth middleware. */
  mfa_source?: 'idp' | 'totp';
  /** Whether this account has a local password. False for accounts created by
   *  SSO, which never get one. The MFA enrollment screen reads this: asking a
   *  federated user to "confirm your password" traps them, because the screen
   *  is unskippable and there is no password to confirm. */
  has_password?: boolean;
  /** The password in use is a temporary one an administrator set: the shell
   *  shows the change-password screen before anything else. From the
   *  `must_change_password` JWT claim, so it survives a silent refresh. */
  must_change_password?: boolean;
  /** Cross-tenant platform-operator SCOPE, from the `is_platform_operator` JWT
   *  claim. Undefined means the token predates the claim, and callers fall back
   *  to `role === 'superadmin'` — see `usePermission().isPlatformOperator`.
   *
   *  This is deliberately NOT part of the effective-permission document: a
   *  permission profile is tenant-scoped and cannot express authority over all
   *  tenants (docs/RBAC_MIGRATION_PLAN.md §2.3). Scope comes from the token,
   *  capabilities come from the profile. */
  is_platform_operator?: boolean;
}

export interface UserPreferences {
  home_app_id: string | null;
  home_dashboard_id: string | null;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  preferences: UserPreferences;
  loading: boolean;
  error: string | null;

  login: (email: string, password: string, tenantId?: string, totpCode?: string) => Promise<void>;
  loginWithOIDC: (provider: 'google' | 'okta' | 'azure') => void;
  handleOIDCCallback: (token: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  /** Replace the account's password (the temporary one included) and adopt
   *  the session the server answers with. Throws with the server's detail. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setMfaEnabled: (enabled: boolean) => void;
  fetchPreferences: () => Promise<void>;
  setHome: (kind: 'app' | 'dashboard', appId: string | null) => Promise<void>;
}

// Access token and tenant_id live in memory only (never localStorage)
let _inMemoryToken: string | null = null;
let _tenantId: string = 'tenant-001';

const EMPTY_PREFS: UserPreferences = { home_app_id: null, home_dashboard_id: null };

// ── Silent in-session token refresh ────────────────────────────────────────
// The access token lives in memory and expires mid-session. Without this the
// app only recovered on a full page reload (TenantContext refreshes on mount),
// so live sessions hit 401 "Signature has expired". Schedule a refresh ~60s
// before expiry so a live session never hits that 401.
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Decode a JWT payload without verifying the signature. JWT segments are
 *  base64url-encoded — normalize before atob. Returns null if undecodable. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decodeJwtPayload(token: string): any | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** The JWT `exp` claim (epoch seconds), or null. */
function getTokenExp(token: string): number | null {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === 'number' ? exp : null;
}

// Only the impersonation TARGET is persisted (who is being impersonated), never
// a token: a JWT in sessionStorage is readable by any script on the origin
// (Sep 2026 pentest AUTH-VULN-06). On reload the target is re-minted from the
// superadmin's HttpOnly cookie, exactly like an expired token is renewed.
const IMP_TARGET_KEY = '_nexus_impersonation_target';
/** Legacy keys from builds that stored the tokens themselves; cleared on sight. */
const LEGACY_IMP_KEYS = ['_nexus_impersonation_token', '_nexus_original_token'];
/** Set just before the reload that ends an impersonation we could not renew,
 *  so the shell can tell the user why they are back in their own session. */
export const IMP_EXPIRED_KEY = '_nexus_impersonation_expired';

/** Single source of truth for "is an impersonated session active?". */
interface ImpersonationTarget { sub: string; tenant_id: string }
function readImpersonationTarget(): ImpersonationTarget | null {
  try {
    const raw = sessionStorage.getItem(IMP_TARGET_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Partial<ImpersonationTarget>;
    return t.sub && t.tenant_id ? { sub: t.sub, tenant_id: t.tenant_id } : null;
  } catch { return null; }
}
function clearImpersonationStorage() {
  sessionStorage.removeItem(IMP_TARGET_KEY);
  for (const k of LEGACY_IMP_KEYS) sessionStorage.removeItem(k);
}
const isImpersonating = () => readImpersonationTarget() !== null;

// Bumped on logout so an in-flight refresh that resolves afterwards cannot
// silently resurrect the session the user just ended.
let _authEpoch = 0;

function clearRefreshTimer() {
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

// Single-flight: the backend rotates the refresh token on every use, so two
// concurrent /auth/refresh calls race — the loser's (already-rotated) token
// gets rejected and would wipe the session. All callers share one in-flight
// refresh instead.
let _refreshInFlight: Promise<boolean> | null = null;

/** (Re)schedule a silent refresh ~60s before the access token expires. Works
 *  for an impersonated session too: `refresh()` renews the impersonation there
 *  instead of falling back to the superadmin's cookie session. */
function scheduleTokenRefresh(token: string) {
  clearRefreshTimer();
  const exp = getTokenExp(token);
  if (exp == null) return;
  // Random jitter (0-20s earlier) de-synchronizes multiple open tabs: the
  // backend rotates the refresh token on every use, so two tabs refreshing
  // at the same instant would race and the loser would be logged out.
  // The 30s floor avoids hammering /auth/refresh if the TTL is ever tiny.
  const jitter = Math.floor(Math.random() * 20_000);
  const msUntil = Math.max(30_000, exp * 1000 - Date.now() - 60_000 - jitter);
  _refreshTimer = setTimeout(() => {
    useAuthStore.getState().refresh().catch(() => undefined);
  }, msUntil);
}

// ── Impersonation lifecycle ────────────────────────────────────────────────
// An impersonated session is a bare access token: `/auth/impersonate` issues no
// refresh cookie of its own, so the only cookie in the browser is the
// *superadmin's*. Spending it while impersonating swaps the live identity — and
// `_tenantId` with it — back to the superadmin while the UI still renders the
// impersonated tenant, and every write from then on lands in the wrong tenant.
// That is exactly how object types created under an impersonated tenant ended
// up in tenant-001. So the cookie is never applied to the store during an
// impersonation; it is only ever spent to re-mint a new impersonation token.

/** Adopt an impersonation JWT as the live session: it — not the superadmin's
 *  cookie session — is what `_tenantId` and every request header derive from. */
export function applyImpersonationToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.tenant_id || !payload.sub) return false;
  sessionStorage.setItem(IMP_TARGET_KEY, JSON.stringify({ sub: payload.sub, tenant_id: payload.tenant_id }));
  for (const k of LEGACY_IMP_KEYS) sessionStorage.removeItem(k);
  _inMemoryToken = token;
  _tenantId = payload.tenant_id;
  scheduleTokenRefresh(token);
  useAuthStore.setState({
    accessToken: token,
    user: {
      id: payload.sub,
      email: payload.email,
      name: payload.name || payload.email,
      role: payload.role as UserRole,
      tenant_id: payload.tenant_id,
      modules: (payload.modules as string[]) || [],
      // Scope travels with the token, so it has to be re-read on every apply:
      // this helper now builds the impersonated user for BOTH the restore and
      // the renewal path, and dropping the claim here would quietly demote
      // every impersonated session to the role-based fallback.
      is_platform_operator: payload.is_platform_operator,
      impersonated_by: payload.impersonated_by,
      mfa_enabled: !!payload.mfa_enabled,
      mfa_source: payload.mfa_source as 'idp' | 'totp' | undefined,
      has_password: payload.has_password !== false,
      must_change_password: payload.must_change_password === true,
    },
  });
  return true;
}

/** Leave an impersonated session for good. The reload is deliberate: it is the
 *  only way to guarantee that no already-mounted view keeps showing (and
 *  writing to) the impersonated tenant after the identity has changed. */
export function endImpersonation(reason?: 'expired') {
  // Close the server-side record (impersonation.end in the audit log). The
  // token itself dies within its short TTL: nothing re-mints it once the
  // target is cleared. keepalive lets the request outlive the reload below.
  const token = _inMemoryToken;
  if (token && reason !== 'expired') {
    fetch(`${AUTH_API}/auth/impersonate/end`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => undefined);
  }
  clearRefreshTimer();
  clearImpersonationStorage();
  if (reason === 'expired') sessionStorage.setItem(IMP_EXPIRED_KEY, '1');
  window.location.reload();
}

/** Spend the refresh cookie for a new access token **without** touching store
 *  state. Callers decide whose session that token becomes. */
async function mintTokenFromCookie(): Promise<{ token: string | null; dead: boolean }> {
  try {
    const res = await fetch(`${AUTH_API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refresh_token: null }),
    });
    // 401/403 is definitive — the cookie is invalid or revoked. Anything else
    // (5xx, 429, network) is transient and must not end the session.
    if (!res.ok) return { token: null, dead: res.status === 401 || res.status === 403 };
    const data = await res.json();
    return { token: data.access_token ?? null, dead: false };
  } catch {
    return { token: null, dead: false };
  }
}

/** Re-mint the impersonation token for the same target user. The superadmin's
 *  cookie session is what authorizes it, so an impersonation lives exactly as
 *  long as that session does — and every renewal writes its own
 *  `impersonation.start` row to the audit log. */
async function renewImpersonation(epoch: number): Promise<boolean> {
  const claims = readImpersonationTarget();
  if (!claims) {
    endImpersonation('expired');
    return false;
  }
  const { token: adminToken, dead } = await mintTokenFromCookie();
  if (!adminToken) {
    // Transient failure: keep the impersonated session and let the next
    // request try again. Only a dead cookie ends it.
    if (dead) endImpersonation('expired');
    return false;
  }
  try {
    const res = await fetch(`${AUTH_API}/auth/impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
      body: JSON.stringify({ target_user_id: claims.sub, target_tenant_id: claims.tenant_id }),
    });
    if (!res.ok) {
      // The superadmin lost the role, or the target user is gone. Either way
      // this impersonation cannot continue.
      endImpersonation('expired');
      return false;
    }
    const data = await res.json();
    if (epoch !== _authEpoch) return false; // user logged out mid-flight
    return applyImpersonationToken(data.access_token);
  } catch {
    return false; // network blip — retried by the next request
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  preferences: EMPTY_PREFS,
  loading: false,
  error: null,

  login: async (email, password, tenantId = 'tenant-001', totpCode) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${AUTH_API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, tenant_id: tenantId, totp_code: totpCode ?? null }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Login failed');
      }

      const data = await res.json();
      _inMemoryToken = data.access_token;
      _tenantId = data.user?.tenant_id || 'tenant-001';
      scheduleTokenRefresh(data.access_token);
      set({
        user: {
          ...data.user,
          modules: (data.user?.modules as string[]) || [],
          mfa_enabled: !!data.user?.mfa_enabled,
          // Read from the token, not the login body: the claim is what every
          // backend check reads, and it survives silent refresh.
          is_platform_operator: decodeJwtPayload(data.access_token)?.is_platform_operator,
          mfa_source: decodeJwtPayload(data.access_token)?.mfa_source as 'idp' | 'totp' | undefined,
          has_password: decodeJwtPayload(data.access_token)?.has_password !== false,
          must_change_password: decodeJwtPayload(data.access_token)?.must_change_password === true,
        },
        accessToken: data.access_token,
        loading: false,
      });
      get().fetchPreferences().catch(() => undefined);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Login failed';
      set({ loading: false, error: msg });
      throw e;
    }
  },

  loginWithOIDC: (provider) => {
    window.location.href = `${AUTH_API}/auth/oidc/${provider}`;
  },

  handleOIDCCallback: async (token: string) => {
    _inMemoryToken = token;
    scheduleTokenRefresh(token);
    const payload = decodeJwtPayload(token);
    if (payload) {
      _tenantId = payload.tenant_id || 'tenant-001';
      set({
        accessToken: token,
        user: {
          id: payload.sub,
          email: payload.email,
          name: payload.name || payload.email,
          role: payload.role as UserRole,
          tenant_id: payload.tenant_id,
          modules: (payload.modules as string[]) || [],
          is_platform_operator: payload.is_platform_operator,
          impersonated_by: payload.impersonated_by,
          mfa_enabled: !!payload.mfa_enabled,
          mfa_source: payload.mfa_source as 'idp' | 'totp' | undefined,
          has_password: payload.has_password !== false,
          must_change_password: payload.must_change_password === true,
        },
      });
      get().fetchPreferences().catch(() => undefined);
    } else {
      set({ error: 'Failed to parse auth token' });
    }
  },

  refresh: async () => {
    if (_refreshInFlight) return _refreshInFlight;
    const epoch = _authEpoch;
    _refreshInFlight = (async () => {
      try {
        // The refresh cookie belongs to the signed-in superadmin, never to the
        // impersonated user. Spending it here would silently swap identity and
        // tenant mid-session, so renew the impersonation instead.
        if (isImpersonating()) return await renewImpersonation(epoch);

        const { token, dead } = await mintTokenFromCookie();
        if (!token) {
          if (dead) {
            // Definitive: the refresh cookie is invalid or revoked.
            clearRefreshTimer();
            set({ user: null, accessToken: null, preferences: EMPTY_PREFS });
            _inMemoryToken = null;
            _tenantId = 'tenant-001';
          } else if (_inMemoryToken) {
            // Transient backend failure (5xx/429): keep the live session and
            // retry shortly instead of hard-logging the user out on a blip.
            clearRefreshTimer();
            _refreshTimer = setTimeout(() => {
              useAuthStore.getState().refresh().catch(() => undefined);
            }, 30_000);
          }
          return false;
        }
        if (epoch !== _authEpoch) return false; // user logged out mid-flight
        _inMemoryToken = token;
        scheduleTokenRefresh(token);

        // Decode user info from the new JWT
        const payload = decodeJwtPayload(token);
        if (payload) {
          _tenantId = payload.tenant_id || 'tenant-001';
          set({
            accessToken: token,
            user: {
              id: payload.sub,
              email: payload.email,
              name: payload.name || payload.email,
              role: payload.role as UserRole,
              tenant_id: payload.tenant_id,
              modules: (payload.modules as string[]) || [],
          is_platform_operator: payload.is_platform_operator,
              impersonated_by: payload.impersonated_by,
              mfa_enabled: !!payload.mfa_enabled,
              mfa_source: payload.mfa_source as 'idp' | 'totp' | undefined,
              has_password: payload.has_password !== false,
              must_change_password: payload.must_change_password === true,
            },
          });
          get().fetchPreferences().catch(() => undefined);
        } else {
          set({ accessToken: token });
        }
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      _refreshInFlight = null;
    });
    return _refreshInFlight;
  },

  logout: async () => {
    const token = _inMemoryToken;
    _inMemoryToken = null;
    _tenantId = 'tenant-001';
    // Drop the impersonation target too: it outlives the store, so leaving it
    // behind would let the next page load resurrect the session just ended.
    clearImpersonationStorage();
    clearRefreshTimer();
    _authEpoch += 1; // invalidate any in-flight refresh
    set({ user: null, accessToken: null, preferences: EMPTY_PREFS });
    try {
      await fetch(`${AUTH_API}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refresh_token: token }),
      });
    } catch {
      // best-effort
    }
  },

  clearError: () => set({ error: null }),

  changePassword: async (currentPassword, newPassword) => {
    const res = await fetch(`${AUTH_API}/auth/change-password`, {
      method: 'POST',
      headers: authedHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to change password');
    }
    const data = await res.json();
    const payload = decodeJwtPayload(data.access_token);
    _inMemoryToken = data.access_token;
    scheduleTokenRefresh(data.access_token);
    const prev = get().user;
    set({
      accessToken: data.access_token,
      user: prev && payload ? {
        ...prev,
        mfa_enabled: !!payload.mfa_enabled,
        mfa_source: payload.mfa_source as 'idp' | 'totp' | undefined,
        must_change_password: payload.must_change_password === true,
      } : prev,
    });
  },

  // Optimistically reflect a just-completed MFA enrollment. The JWT still says
  // mfa_enabled=false until the next silent refresh re-mints it, so flip the
  // in-memory user here to update the UI immediately.
  setMfaEnabled: (enabled: boolean) =>
    set((s) => (s.user ? { user: { ...s.user, mfa_enabled: enabled } } : {})),

  fetchPreferences: async () => {
    if (!_inMemoryToken) return;
    try {
      const res = await fetch(`${AUTH_API}/auth/users/me/preferences`, {
        headers: authedHeaders(),
      });
      if (!res.ok) return;
      const data = (await res.json()) as UserPreferences;
      set({
        preferences: {
          home_app_id: data.home_app_id ?? null,
          home_dashboard_id: data.home_dashboard_id ?? null,
        },
      });
    } catch {
      // non-fatal — fall back to legacy slug-based home
    }
  },

  setHome: async (kind, appId) => {
    const current = get().preferences;
    const next: UserPreferences = {
      home_app_id: kind === 'app' ? appId : current.home_app_id,
      home_dashboard_id: kind === 'dashboard' ? appId : current.home_dashboard_id,
    };
    // Optimistic update so the UI flips before the round-trip lands.
    set({ preferences: next });
    try {
      const res = await fetch(`${AUTH_API}/auth/users/me/preferences`, {
        method: 'PUT',
        headers: authedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(next),
      });
      if (!res.ok) {
        set({ preferences: current });
        throw new Error('Failed to update home preference');
      }
    } catch (e) {
      set({ preferences: current });
      throw e;
    }
  },
}));

// ── Restore an impersonation on page load ─────────────────────────────────
(() => {
  for (const k of LEGACY_IMP_KEYS) sessionStorage.removeItem(k);
  const target = readImpersonationTarget();
  if (!target) return;
  // No token survives a reload on purpose. Pin the tenant to the target right
  // away so nothing can write to the superadmin's tenant in the meantime, then
  // re-mint the impersonation from the cookie — the same path an expired
  // token takes. Until it lands, requests 401 and authFetch retries through
  // refresh(), which is single-flight with this call.
  _tenantId = target.tenant_id;
  useAuthStore.getState().refresh()
    .then((ok) => { if (ok) useAuthStore.getState().fetchPreferences().catch(() => undefined); })
    .catch(() => undefined);
})();

// ── Refresh on tab refocus ─────────────────────────────────────────────────
// Background tabs throttle setTimeout, so the scheduled refresh can fire late.
// When the tab becomes visible again, refresh immediately if the token is
// within 60s of expiry (or already expired) — before any request can 401.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const token = _inMemoryToken;
    if (!token) return;
    const exp = getTokenExp(token);
    if (exp != null && exp * 1000 - Date.now() < 60_000) {
      useAuthStore.getState().refresh().catch(() => undefined);
    }
  });
}

/** Get the current in-memory access token for API calls. */
export const getAccessToken = () => _inMemoryToken;

/** Standard auth header bundle for fetch calls. Includes Authorization
 *  (when signed in) and x-tenant-id so backend services can route. */
export function authedHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { 'x-tenant-id': _tenantId, ...extra };
  if (_inMemoryToken) h['Authorization'] = `Bearer ${_inMemoryToken}`;
  return h;
}

/** Get the current tenant_id for the logged-in user. */
export const getTenantId = () => _tenantId;

/** Get the current user ID for the logged-in user. */
export const getUserId = (): string | undefined => useAuthStore.getState().user?.id;

/** Get the current allowed modules for the logged-in user. */
export function getModules(): string[] {
  return useAuthStore.getState().user?.modules ?? [];
}
